// Shared comma-formatting number input. Consolidates the previously duplicated
// helpers in operations, financial-statements, marketplace-listing-form, and
// onboarding. Live-formats with US thousands separators while preserving the
// caret position, and reports both the raw string and parsed number upward.
import { useEffect, useRef, useState, type InputHTMLAttributes, type ChangeEvent, type FocusEvent } from "react";
import { Input } from "@/components/ui/input";
import { stripMoneyCommas } from "@/lib/moneyInput";

type FormattedNumberInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "prefix" | "min" | "max"
> & {
  /** Current value from the parent (number, raw numeric string, or empty). */
  value: string | number | null;
  /**
   * Called on every edit with the raw (comma-free) string and the parsed
   * number (null when the field is empty or not yet a valid number).
   */
  onValueChange: (raw: string, num: number | null) => void;
  /** Max decimal places allowed. 0 = integers only. Undefined = unlimited. */
  decimals?: number;
  /** Whether a leading minus sign is allowed. Default true. */
  allowNegative?: boolean;
  /** Clamp the parsed value into [min, max] on blur. */
  min?: number;
  max?: number;
  /** Optional adornments rendered inside a relative wrapper. */
  prefix?: string;
  suffix?: string;
  testId?: string;
};

function formatRaw(raw: string): string {
  if (raw === "" || raw === "-") return raw;
  const m = raw.match(/^(-?)(\d*)(\.\d*)?$/);
  if (!m) return raw;
  const [, sign, intPart, decPart] = m;
  const grouped = intPart ? Number(intPart).toLocaleString("en-US") : "";
  return sign + grouped + (decPart ?? "");
}

function toRawString(value: string | number | null): string {
  if (value === null || value === undefined || value === "") return "";
  return stripMoneyCommas(String(value));
}

export function FormattedNumberInput({
  value,
  onValueChange,
  decimals,
  allowNegative = true,
  min,
  max,
  prefix,
  suffix,
  testId,
  onBlur,
  className,
  inputMode,
  ...props
}: FormattedNumberInputProps) {
  const [display, setDisplay] = useState<string>(() => formatRaw(toRawString(value)));
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const isFocused = useRef(false);

  // Restore caret after a formatting-induced re-render.
  useEffect(() => {
    if (pendingCaret.current !== null && inputRef.current) {
      const pos = pendingCaret.current;
      pendingCaret.current = null;
      inputRef.current.setSelectionRange(pos, pos);
    }
  });

  // Sync display when the parent value changes externally (not while typing).
  useEffect(() => {
    if (isFocused.current) return;
    setDisplay(formatRaw(toRawString(value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const sanitize = (input: string): string => {
    let cleaned = input.replace(allowNegative ? /[^0-9.\-]/g : /[^0-9.]/g, "");
    // Keep at most one leading minus sign.
    if (allowNegative) {
      const negative = cleaned.startsWith("-");
      cleaned = cleaned.replace(/-/g, "");
      if (negative) cleaned = "-" + cleaned;
    }
    if (decimals === 0) return cleaned.replace(/\./g, "");
    // Keep only the first dot.
    const firstDot = cleaned.indexOf(".");
    if (firstDot >= 0) {
      let dec = cleaned.slice(firstDot + 1).replace(/\./g, "");
      if (decimals !== undefined) dec = dec.slice(0, decimals);
      cleaned = cleaned.slice(0, firstDot + 1) + dec;
    }
    return cleaned;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const selStart = el.selectionStart ?? el.value.length;
    // Track caret by counting "significant" characters (digits, plus the
    // decimal point when decimals are allowed). Counting digits alone put the
    // caret *before* a just-typed decimal point, so the next digits landed in
    // front of the dot.
    const isSignificant = (ch: string) => /\d/.test(ch) || (decimals !== 0 && ch === ".");
    let sigBeforeCaret = 0;
    for (const ch of el.value.slice(0, selStart)) {
      if (isSignificant(ch)) sigBeforeCaret++;
    }

    const raw = sanitize(stripMoneyCommas(el.value));
    const formatted = formatRaw(raw);

    let newCaret = formatted.length;
    if (sigBeforeCaret === 0) {
      newCaret = raw.startsWith("-") ? Math.min(1, formatted.length) : 0;
    } else {
      let sigCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (isSignificant(formatted[i])) {
          sigCount++;
          if (sigCount === sigBeforeCaret) {
            newCaret = i + 1;
            break;
          }
        }
      }
    }
    pendingCaret.current = newCaret;
    setDisplay(formatted);

    const parsed = parseFloat(raw);
    onValueChange(raw, Number.isFinite(parsed) ? parsed : null);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    isFocused.current = false;
    const raw = stripMoneyCommas(display);
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed) || raw === "" || raw === "-") {
      setDisplay("");
      if (raw !== "") onValueChange("", null);
    } else {
      let clamped = parsed;
      if (min !== undefined && clamped < min) clamped = min;
      if (max !== undefined && clamped > max) clamped = max;
      setDisplay(formatRaw(String(clamped)));
      if (clamped !== parsed) onValueChange(String(clamped), clamped);
    }
    onBlur?.(e);
  };

  const input = (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode={inputMode ?? (decimals === 0 ? "numeric" : "decimal")}
      value={display}
      onChange={handleChange}
      onFocus={(e) => {
        isFocused.current = true;
        props.onFocus?.(e);
      }}
      onBlur={handleBlur}
      className={`${prefix ? "pl-7 " : ""}${suffix ? "pr-14 " : ""}${className ?? ""}`.trim() || undefined}
      data-testid={testId ?? (props as Record<string, unknown>)["data-testid"] as string | undefined}
    />
  );

  if (!prefix && !suffix) return input;

  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">
          {prefix}
        </span>
      )}
      {input}
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}
